import useApi from '@/Composables/useApi'
import { usePodcasterStore } from '@/Stores/podcaster'
import type {
    ClipFinderConversationResource,
    ClipFinderMessageResource,
    ClipResource,
    ClipSuggestionResource,
    LLMModelResource,
    PodcasterResource,
} from '@/types'

import { defineStore, storeToRefs } from 'pinia'
import type { ComputedRef, Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/Stores/user'
import { Ziggy } from './../ziggy'
import { route } from 'ziggy-js'
import { router } from '@inertiajs/vue3'

const { getJson, postJson, patchJson } = useApi()

export enum ScrollState {
    Idle = 'idle',
    Requesting = 'requesting',
    Waiting = 'waiting',
    Shown = 'shown',
    Ended = 'end',
    Failed = 'failed',
}

export const defineConversationStore = (
    initialConversation: ClipFinderConversationResource | undefined = undefined,
    shared: boolean | undefined = false
): ReturnType<typeof useConversationStore> => {
    const store = useConversationStore()
    store.load(initialConversation, shared || false)
    store.fetchHistory()
    return store
}

export const useConversationStore = defineStore('conversation', () => {
    const podcasterStore = usePodcasterStore()
    const userStore = useUserStore()

    const { loggedIn } = storeToRefs(userStore)
    const { selectedPodcasters } = storeToRefs(podcasterStore)

    const conversationId: Ref<string | null> = ref<string | null>(null)
    const today = new Date()
    const fromYear: Ref<number> = ref(2007)
    const tillYear: Ref<number> = ref(today.getFullYear())

    const conversations: Ref<ClipFinderConversationResource[]> = ref<ClipFinderConversationResource[]>([])
    const messages: Ref<ClipFinderMessageResource[]> = ref([])
    const scrollState: Ref<ScrollState> = ref<ScrollState>(ScrollState.Idle)
    const orchestrating: Ref<Boolean> = ref<Boolean>(false)

    const configurationSlideOver = ref(false)
    const shared = ref<boolean>(false)
    const messageCallbacks: Ref<Function[]> = ref([])
    const clipCallbacks: Ref<Function[]> = ref([])

    const loadingText: Ref<String> = ref(null)

    const searchEarlierClips = async (message: ClipFinderMessageResource) => {
        let text = `No more results found in the range ${fromYear.value} - ${tillYear.value}. Now searching for clips from `
        let tempFromYear = fromYear.value
        let tempTillYear = tillYear.value
        tillYear.value = fromYear.value
        loadingText.value = text + fromYear.value
        scrollState.value = ScrollState.Requesting

        let from_year = (message.from_year ? message.from_year : fromYear.value) - 1
        fromYear.value = from_year

        await patchConversation({ from_year: from_year, till_year: from_year })

        await sendMessage(message.original_question)

        fromYear.value = tempFromYear
        tillYear.value = tempTillYear

        await patchConversation({ from_year: fromYear.value, till_year: tillYear.value })
    }

    const fetchHistory = async () => {
        if (conversations.value.length > 0) {
            return
        }

        conversations.value = await getJson('/api/conversations')
    }

    const load = (initialConversation: ClipFinderConversationResource | undefined, initialShared: boolean) => {
        shared.value = initialShared

        if (!initialConversation) {
            conversationId.value = null
            messages.value = []
            if (fromYear.value === undefined || fromYear.value === null) {
                fromYear.value = 2007
            }
            if (tillYear.value === undefined || tillYear.value === null) {
                tillYear.value = new Date().getFullYear()
            }
            return
        }

        conversationId.value = initialConversation.id
        messages.value = initialConversation.messages
        if (fromYear.value === undefined || fromYear.value === null) {
            fromYear.value = initialConversation.from_year
        }
        if (tillYear.value === undefined || tillYear.value === null) {
            tillYear.value = initialConversation.till_year
        }
    }

    /**
     * A conversation only exists on the server once it has a UUID, and
     * conversationId is not always one. On /conversations/start it is the
     * literal "start"; PodcasterMessage.vue derives it from the last URL
     * segment, so on /conversations/{podcaster-slug} it is that slug; on a
     * content page (podcaster, category) there is none at all. None of those
     * can be subscribed to or shared.
     *
     * Only the watcher below and the share button use this so far. Other call
     * sites still test conversationId for truthiness, which is weaker; see
     * patchConversation and updateParticipants.
     */
    const hasPersistedConversation: ComputedRef<boolean> = computed(
        (): boolean => conversationId.value !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(conversationId.value)
    )

    watch(conversationId, () => {
        // Subscribing to a channel for a non-persisted id makes the channel's
        // route-model binding throw ModelNotFound and the auth call return 404.
        if (!hasPersistedConversation.value) {
            return
        }

        if (loggedIn.value) {
            const channel = 'clip-conversations.' + conversationId.value
            window.Echo.leave(channel)
            window.Echo.private('clip-conversations.' + conversationId.value)
                .listen('.clip-updated', onClipUpdated)
                .listen('.message-updated', onMessageUpdated)
        } else {
            const channel = 'clip-conversations.' + conversationId.value
            window.Echo.leave(channel)
            window.Echo.channel('session-clip-conversations.' + conversationId.value)
                .listen('.clip-updated', onClipUpdated)
                .listen('.message-updated', onMessageUpdated)
        }

        // Not on a shared page: the visitor opened /share/{id}, which is open to
        // whoever has the link, and the conversation page behind it is not. Since
        // the policy was restored (PR 184) that navigation answers 403 for
        // everyone the link was shared with (CLIP-45).
        if (shared.value) {
            return
        }

        navigateToCurrentConversation()
    })

    async function onMessageUpdated(e: ClipFinderMessageResource) {
        const current: ClipFinderMessageResource[] = messages.value
        const response = await getJson(`/api/messages/${e.id}`)

        for (const key in current) {
            const currentMessage = current[key]

            if (currentMessage.id === e.id) {
                current[key] = {
                    ...currentMessage,
                    ...response,
                }

                // Don't end the scroll just because suggestions arrived (they show
                // after the first batch while more clips may still be unscreened).
                // Only screen-more's 'finished' result ends it.
                if (scrollState.value !== ScrollState.Ended) {
                    scrollState.value = ScrollState.Waiting
                }

                messageCallbacks.value.forEach((callback) => callback())
                messageCallbacks.value = []

                return
            }
        }
    }

    async function onClipUpdated(e: { id: string; message_id: string }) {
        const current: ClipFinderMessageResource[] = messages.value
        const response: ClipResource = await getJson(`/api/clips/${e.id}`)

        for (const key in current) {
            if (current[key].id === e.message_id) {
                for (const clipKey in current[key].clips) {
                    let currentClip = current[key].clips[clipKey]

                    if (currentClip.id === e.id) {
                        current[key].clips[clipKey] = {
                            ...currentClip,
                            ...response,
                        }

                        clipCallbacks.value.forEach((callback) => callback())
                        clipCallbacks.value = []

                        return
                    }
                }
            }
        }

        messages.value = current
    }

    function addMessages(newMessages: ClipFinderMessageResource[]) {
        messages.value.push(...newMessages)
    }

    async function sendMessage(question: string, alwaysThrow: Boolean = false) {
        if (shared.value) {
            return startOwnConversation(question, alwaysThrow)
        }

        return sendMessageToApi(question, conversationId.value, selectedPodcasters.value, alwaysThrow)
    }

    /**
     * A visitor on a shared page does not own the conversation they are reading.
     * Their question opens a conversation of their own, with the podcasters and
     * years they are looking at, instead of failing on someone else's (CLIP-46).
     *
     * Sending no conversation id is what makes the server start a fresh one and
     * put it in this visitor's session. The page stays a shared page until that
     * has actually happened: `shared` only goes off once there is a conversation
     * to go to, and a failed attempt puts back what was on screen. Otherwise a
     * network hiccup would leave the visitor on someone else's page that had
     * quietly turned into an owned one.
     */
    async function startOwnConversation(question: string, alwaysThrow: Boolean = false) {
        const readingId = conversationId.value
        const readingMessages = messages.value
        const readingScrollState = scrollState.value

        conversationId.value = null
        messages.value = []

        // scrollState goes back too: sendMessageToApi leaves it on Failed, and
        // ClipConversation renders "We couldn't process your query due to an
        // internal error" on that, which is the very message this ticket is
        // about. Putting the page back means putting that away as well.
        const restore = () => {
            conversationId.value = readingId
            messages.value = readingMessages
            scrollState.value = readingScrollState
        }

        let own

        try {
            own = await sendMessageToApi(question, null, selectedPodcasters.value, alwaysThrow)
        } catch (e: any) {
            restore()

            throw e
        }

        if (!own) {
            restore()

            return
        }

        // Theirs now, so the navigation CLIP-45 suppresses on a shared page is
        // exactly what should happen here.
        shared.value = false
        navigateToCurrentConversation()

        return own
    }

    async function sendMessageToApi(
        question: string,
        conversationUuid: string | null,
        podcasters: Array<PodcasterResource>,
        alwaysThrow: Boolean = false
    ) {
        scrollState.value = ScrollState.Idle

        if (podcasters.length === 0) {
            return Promise.reject('No podcaster selected')
        }

        try {
            scrollState.value = ScrollState.Requesting
            const { response, headers } = await postJson(
                `/api/conversations`,
                {
                    from: fromYear.value,
                    till: tillYear.value,
                    podcasters: podcasters.map((p: PodcasterResource) => p.id),
                    question: question,
                },
                {
                    'X-Conversation-Id': conversationUuid || '',
                }
            )

            conversationId.value = headers.get('X-Conversation-Id') || null
            addMessages([response as ClipFinderMessageResource])

            addOrUpdateConversation(question)

            return conversationId.value
        } catch (e: any) {
            if (e.status && e.status === 422) {
                const errors = await e.json()

                scrollState.value = ScrollState.Idle
                return Promise.reject(errors)
            }

            if (alwaysThrow) {
                throw e
            }

            scrollState.value = ScrollState.Failed
            return
        }
    }

    async function updateParticipants(podcasters: string[]) {
        // Same as the years: on a shared page this is the visitor picking who
        // their own conversation will be about (CLIP-46). The slide-over applies
        // the selection locally either way.
        if (shared.value) {
            return
        }

        const { response } = await postJson(`/api/conversations/${conversationId.value}/participants`, {
            participants: podcasters,
        })

        const updatedConversation: ClipFinderConversationResource = response as ClipFinderConversationResource

        for (const i in conversations.value) {
            if (conversations.value[i].id === updatedConversation.id) {
                conversations.value[i].participants = updatedConversation.participants
            }
        }
    }

    function addOrUpdateConversation(question: string) {
        if (
            conversations.value.filter((conversation: ClipFinderConversationResource): boolean => conversation.id === conversationId.value).length > 0
        ) {
            for (const i in conversations.value) {
                if (conversations.value[i].id === conversationId.value) {
                    conversations.value[i].excerpt = question
                    conversations.value[i].updated_at = new Date().toISOString()
                }
            }

            // Re-sort the conversations
            conversations.value = conversations.value.sort((a, b) => new Date(b.updated_at).valueOf() - new Date(a.updated_at).valueOf())
            return
        }

        conversations.value.unshift({
            id: conversationId.value,
            participants: selectedPodcasters.value,
            excerpt: question,
            messages_count: 2,
            updated_at: Date.now(),
        } as ClipFinderConversationResource)
    }

    async function continueBySuggestedTopic(suggestion: ClipSuggestionResource) {
        // The suggestion belongs to the conversation on screen. On a shared page
        // that is someone else's, so only the question travels: sendMessage opens
        // the visitor's own conversation with it, and the suggestion is not
        // ticked off, not even in this visitor's view (CLIP-46).
        if (shared.value) {
            await sendMessage(suggestion.topic)

            return
        }

        suggestion.selected = true

        await Promise.all([postJson(`/api/clips/suggestions/${suggestion.id}`), sendMessage(suggestion.topic)])
    }

    async function loadMoreClips() {
        if (scrollState.value === ScrollState.Ended) {
            return
        }

        if (messages.value.length === 0) {
            return
        }

        const message: ClipFinderMessageResource = messages.value[messages.value.length - 1]

        if (!message) {
            scrollState.value = ScrollState.Ended
            return
        }

        // Note: suggestions appearing does NOT mean we're done — there may still be
        // unscreened clips in the pool. screen-more returns 'finished' (→ Ended) when
        // the pool is actually exhausted.
        await runAgent(message.id)
    }

    async function runAgent(messageId: string) {
        // Screen the next batch of preselected clips on demand (scroll), rather than
        // re-running the whole ClipFinder. Returns 'finished' when the candidate
        // pool / screening cap is exhausted.
        await orchestrate(messageId, 'screen-more')
    }

    async function orchestrate(messageId: string, endpoint: string) {
        if (shared.value) {
            return
        }

        if (orchestrating.value) {
            return
        }

        orchestrating.value = true

        try {
            const {
                response: { status },
            } = await postJson(`/api/messages/${messageId}/${endpoint}/`)

            if (status === 'finished') {
                scrollState.value = ScrollState.Ended
            } else {
                scrollState.value = ScrollState.Waiting
            }
        } catch {
            // A failed screen-more/retry must not leave the store wedged: surface the
            // failure and (via finally) clear the flag so the user can try again.
            scrollState.value = ScrollState.Failed
        } finally {
            setTimeout(() => (orchestrating.value = false), 500)
        }
    }

    async function retryMessage(messageId: string, callback: Function) {
        messageCallbacks.value.push(callback)

        await orchestrate(messageId, 'retry')
    }

    /**
     * Both of these run an agent on a clip that belongs to the conversation on
     * screen. On a shared page that is someone else's, so they stop here rather
     * than in each component that happens to offer a button (CLIP-46).
     */
    async function retryClip(clipId: string, callback: Function) {
        if (shared.value) {
            return
        }

        clipCallbacks.value.push(callback)

        await postJson(`/api/clips/${clipId}/retry`)
    }

    async function voteClip(clipId: string, vote: Boolean) {
        await postJson(`/api/clips/${clipId}/vote`, { vote: vote })
    }

    async function captionClip(clipId: string, callback: Function) {
        if (shared.value) {
            return
        }

        clipCallbacks.value.push(callback)

        await postJson(`/api/clips/${clipId}/caption`)
    }

    function navigateToCurrentConversation() {
        const url = route('clipfinder.conversations.show', [conversationId.value], undefined, Ziggy)
        window.history.pushState({}, '', route('clipfinder.conversations.show', [conversationId.value], undefined, Ziggy))
        router.visit(url, {
            preserveState: true,
            preserveScroll: true,
            replace: true,
        })
    }

    async function updateConversationModel(model: LLMModelResource) {
        await patchConversation({
            llm_model: model.id,
        })
    }

    async function patchConversation(body: Object) {
        // On a shared page the years are the visitor's own choice for the
        // conversation they are about to start, not an edit of the owner's
        // (CLIP-46). The store keeps them; the server is not told.
        if (shared.value) {
            return
        }

        if (!conversationId.value) {
            return
        }

        await patchJson('/api/conversations/' + conversationId.value, body)
    }

    async function shareConversation(clipId: string | undefined = undefined): Promise<string> {
        let endpoint = '/api/share/' + conversationId.value

        if (clipId) {
            endpoint += '/' + clipId
        }

        const { response } = await postJson(endpoint)

        return response.id
    }

    const lastMessage: ComputedRef<ClipFinderMessageResource | null> = computed(() => messages.value[messages.value.length - 1] || null)

    return {
        fetchHistory,
        load,
        conversations,
        conversationId,
        hasPersistedConversation,
        lastMessage,
        messages,
        scrollState,
        shared,
        fromYear,
        tillYear,
        configurationSlideOver,
        sendMessage,
        sendMessageToApi,
        loadMoreClips,
        continueBySuggestedTopic,
        retryMessage,
        retryClip,
        voteClip,
        captionClip,
        updateConversationModel,
        patchConversation,
        shareConversation,
        updateParticipants,
        searchEarlierClips,
    }
})
